import { TFunction } from 'i18next';
import { ScoreCardType } from '../services/scoreCardService';

export const DEFAULT_SCORE_CARD_TYPE = ScoreCardType.COUNT;

export const isTimeScoreCardType = (type?: ScoreCardType): boolean => {
  return type === ScoreCardType.TIME_HH_MM || type === ScoreCardType.TIME_MM_SS || type === ScoreCardType.TIME_HH_MM_SS;
};

export const getScoreCardTypeLabelKey = (type?: ScoreCardType): string => {
  switch (type ?? DEFAULT_SCORE_CARD_TYPE) {
    case ScoreCardType.GRADE:
      return 'grade';
    case ScoreCardType.TIME_HH_MM:
      return 'time_hh_mm';
    case ScoreCardType.TIME_MM_SS:
      return 'time_mm_ss';
    case ScoreCardType.TIME_HH_MM_SS:
      return 'time_hh_mm_ss';
    case ScoreCardType.PERCENTAGE:
      return 'percentage';
    case ScoreCardType.BOOLEAN:
      return 'boolean';
    case ScoreCardType.COUNT:
    default:
      return 'count';
  }
};

export const getTimeInputPattern = (type?: ScoreCardType): RegExp => {
  switch (type) {
    case ScoreCardType.TIME_HH_MM:
    case ScoreCardType.TIME_MM_SS:
      return /^\d{2}:\d{2}$/;
    case ScoreCardType.TIME_HH_MM_SS:
      return /^\d{2}:\d{2}:\d{2}$/;
    default:
      return /^.*$/;
  }
};

export const getTimeInputPlaceholder = (type?: ScoreCardType): string => {
  switch (type) {
    case ScoreCardType.TIME_HH_MM:
      return 'HH:MM';
    case ScoreCardType.TIME_MM_SS:
      return 'MM:SS';
    case ScoreCardType.TIME_HH_MM_SS:
      return 'HH:MM:SS';
    default:
      return '';
  }
};

export const normalizeTimeInput = (rawValue: string, type?: ScoreCardType): string => {
  const digits = rawValue.replace(/\D/g, '');

  if (type === ScoreCardType.TIME_HH_MM || type === ScoreCardType.TIME_MM_SS) {
    const limited = digits.slice(0, 4);
    if (limited.length <= 2) return limited;
    return `${limited.slice(0, 2)}:${limited.slice(2)}`;
  }

  if (type === ScoreCardType.TIME_HH_MM_SS) {
    const limited = digits.slice(0, 6);
    if (limited.length <= 2) return limited;
    if (limited.length <= 4) return `${limited.slice(0, 2)}:${limited.slice(2)}`;
    return `${limited.slice(0, 2)}:${limited.slice(2, 4)}:${limited.slice(4)}`;
  }

  return rawValue;
};

export const isCompleteTimeInput = (rawValue: string, type?: ScoreCardType): boolean => {
  return getTimeInputPattern(type).test(rawValue);
};

export function formatScore(score: number | undefined, type?: ScoreCardType, t?: TFunction): string {
  if (score === undefined || score === null || Number.isNaN(score)) {
    return '-';
  }

  switch (type ?? DEFAULT_SCORE_CARD_TYPE) {
    case ScoreCardType.COUNT:
      return Math.floor(score).toString();

    case ScoreCardType.GRADE:
      return parseFloat(score.toFixed(2)).toString();

    case ScoreCardType.TIME_HH_MM: {
      const totalSeconds = Math.floor(score);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }

    case ScoreCardType.TIME_MM_SS: {
      const totalSeconds = Math.floor(score);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }

    case ScoreCardType.TIME_HH_MM_SS: {
      const totalSeconds = Math.floor(score);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
        .toString()
        .padStart(2, '0')}`;
    }

    case ScoreCardType.PERCENTAGE:
      return `${Math.round(score)}%`;

    case ScoreCardType.BOOLEAN:
      if (t) {
        return score === 0 ? t('page.community.scores.forms.no') : t('page.community.scores.forms.yes');
      }
      return score === 0 ? '0' : '1';

    default:
      return score.toString();
  }
}

export function parseScore(input: string, type?: ScoreCardType): number {
  const scoreType = type ?? DEFAULT_SCORE_CARD_TYPE;

  switch (scoreType) {
    case ScoreCardType.TIME_HH_MM: {
      const parts = input.split(':');
      if (parts.length === 2 && parts[0] !== undefined && parts[1] !== undefined) {
        const hours = Number.parseInt(parts[0], 10) || 0;
        const minutes = Number.parseInt(parts[1], 10) || 0;
        return hours * 3600 + minutes * 60;
      }
      return Number.parseFloat(input) || 0;
    }

    case ScoreCardType.TIME_MM_SS: {
      const parts = input.split(':');
      if (parts.length === 2 && parts[0] !== undefined && parts[1] !== undefined) {
        const minutes = Number.parseInt(parts[0], 10) || 0;
        const seconds = Number.parseInt(parts[1], 10) || 0;
        return minutes * 60 + seconds;
      }
      return Number.parseFloat(input) || 0;
    }

    case ScoreCardType.TIME_HH_MM_SS: {
      const parts = input.split(':');
      if (parts.length === 3 && parts[0] !== undefined && parts[1] !== undefined && parts[2] !== undefined) {
        const hours = Number.parseInt(parts[0], 10) || 0;
        const minutes = Number.parseInt(parts[1], 10) || 0;
        const seconds = Number.parseInt(parts[2], 10) || 0;
        return hours * 3600 + minutes * 60 + seconds;
      }
      return Number.parseFloat(input) || 0;
    }

    case ScoreCardType.BOOLEAN: {
      const lower = input.toLowerCase().trim();
      if (lower === '1' || lower === 'true' || lower === 'yes') {
        return 1;
      }
      return 0;
    }

    default: {
      const cleaned = input.replace(/,/g, '.').replace(/[^0-9.-]/g, '');
      return Number.parseFloat(cleaned) || 0;
    }
  }
}

export function validateScore(score: number, type?: ScoreCardType): boolean {
  if (Number.isNaN(score)) return false;

  switch (type ?? DEFAULT_SCORE_CARD_TYPE) {
    case ScoreCardType.COUNT:
      return Number.isInteger(score);

    case ScoreCardType.GRADE:
      return score >= 0 && score <= 10;

    case ScoreCardType.PERCENTAGE:
      return score >= 0 && score <= 100;

    case ScoreCardType.BOOLEAN:
      return score === 0 || score === 1;

    case ScoreCardType.TIME_HH_MM:
    case ScoreCardType.TIME_MM_SS:
    case ScoreCardType.TIME_HH_MM_SS:
      return score >= 0;

    default:
      return true;
  }
}

export const formatScoreForInput = (score: number, type?: ScoreCardType): string => {
  if (isTimeScoreCardType(type)) {
    return formatScore(score, type);
  }
  return score.toString();
};

export const getScoreInputProps = (
  type?: ScoreCardType,
): {
  step?: number;
  min?: number;
  max?: number;
} => {
  switch (type ?? DEFAULT_SCORE_CARD_TYPE) {
    case ScoreCardType.COUNT:
      return { step: 1 };
    case ScoreCardType.GRADE:
      return { step: 0.01, min: 0, max: 10 };
    case ScoreCardType.PERCENTAGE:
      return { step: 1, min: 0, max: 100 };
    default:
      return { step: 1 };
  }
};

export const clampScore = (score: number, type?: ScoreCardType): number => {
  switch (type ?? DEFAULT_SCORE_CARD_TYPE) {
    case ScoreCardType.COUNT:
      return Math.floor(score);
    case ScoreCardType.GRADE:
      return Math.max(0, Math.min(10, score));
    case ScoreCardType.PERCENTAGE:
      return Math.max(0, Math.min(100, score));
    case ScoreCardType.BOOLEAN:
      return score > 0 ? 1 : 0;
    case ScoreCardType.TIME_HH_MM:
    case ScoreCardType.TIME_MM_SS:
    case ScoreCardType.TIME_HH_MM_SS:
      return Math.max(0, Math.floor(score));
    default:
      return score;
  }
};
